import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/css-utils'
import { useProfessionField } from '@/modules/onboarding/use-profession-field'
import { formatCompModel } from '@/modules/salary/common/utils'
import { EditReviewLink } from '@/modules/salary/details/edit-review-link'
import { MiniPercentileHistogram } from '@/modules/salary/details/mini-percentile-histogram'
import { SatisfactionBadge } from '@/modules/salary/details/satisfaction-badge'
import { TotalCompAlert } from '@/modules/salary/details/total-comp-alert'
import {
  SalaryRestrictionType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { CompSatisfaction, SalaryDetails } from '@/modules/salary/types'
import { OnboardingFieldNames } from '@/modules/surveys/configs/onboarding/base-config'
import {
  COMPENSATION_COLLECTIONS_PERCENTAGE_ID,
  COMPENSATION_HOURLY_ID,
  COMPENSATION_PRODUCTIVITY_WRVU_ID,
} from '@/modules/surveys/configs/onboarding/compensation-config'
import {
  OnboardingAccountType,
  OnboardingPageIds,
} from '@/modules/surveys/configs/onboarding/constants'

import {
  formatAbbreviatedDollar,
  formatDollar,
} from '@/utils/currency-formatters'
import { sum } from 'lodash'
import { usePostHog } from 'posthog-js/react'
import * as React from 'react'
import { Fragment } from 'react'
import { DetailNote } from './detail-note'

interface SubItem {
  label: string
  value: number
  singleItemLabel?: string
  id?: string
  overtimeRatePerHour?: number
}

interface TotalCompDetailsProps {
  salaryDetails: SalaryDetails
  isOnboardingReviewPane?: boolean
  showHourly?: boolean
  accountType?: OnboardingAccountType
  compSatisfaction?: CompSatisfaction
  isAuthenticated: boolean
  isLocumsOrPrn: boolean
}

function getOvertimeRateLabel(subItem: SubItem) {
  const isOvertime =
    subItem?.id === OnboardingFieldNames.CompensationOtherOvertimeIncome

  if (isOvertime && subItem.overtimeRatePerHour) {
    return ` @ ${formatDollar(subItem.overtimeRatePerHour)}/hr`
  }
  return ''
}

function SalaryGlossary({
  salaryDetails,
  isLocumsOrPrn,
}: {
  salaryDetails: SalaryDetails
  isLocumsOrPrn?: boolean
}) {
  const {
    collectionsPct,
    nightlyHourlyRate,
    weekendHourlyRate,
    hourlyRateDifferential,
    basePayPerHour,
    compModel,
    baseSalary,
    wrvuRate,
    compTargetWrvu,
    locums,
    overtimeRatePerHour,
  } = salaryDetails
  const showCollectionsPct =
    compModel === COMPENSATION_COLLECTIONS_PERCENTAGE_ID && collectionsPct > 0
  const showWrvuRate =
    compModel === COMPENSATION_PRODUCTIVITY_WRVU_ID &&
    wrvuRate &&
    wrvuRate > 0 &&
    compTargetWrvu &&
    compTargetWrvu > 0
  const showHourlyRate = !isLocumsOrPrn && compModel === COMPENSATION_HOURLY_ID

  return (
    <div>
      <div className="flex w-full justify-between">
        <div className="flex space-x-2 items-center">
          <div className="bg-blue-60 w-4 h-4 rounded" />
          <span className="font-medium text-sm text-copy-primary">
            {isLocumsOrPrn ? 'Base Hourly Rate' : 'Base Salary'}
          </span>
        </div>
        <span
          className="font-medium text-sm text-copy-muted"
          data-testid="total-comp-base-salary"
        >
          {isLocumsOrPrn
            ? `${formatDollar(baseSalary)}/hr`
            : formatDollar(baseSalary)}
        </span>
      </div>
      {isLocumsOrPrn ? (
        <>
          <div
            className={cn(
              'text-sm font-medium text-copy-primary pl-4 ml-2 flex flex-col gap-2 mt-2 border-l border-border-muted',
            )}
          >
            {[
              {
                label: 'Overtime',
                value: overtimeRatePerHour,
                enabled: overtimeRatePerHour > 0,
              },
              {
                label: 'Call',
                value: locums?.callRatePerHour,
                enabled: locums?.callRatePerHour > 0,
                subItems: [
                  {
                    label: `${formatDollar(locums?.callStipendWeeklyRate)}/day Weekday Stipend`,
                    enabled: locums?.callStipendWeeklyRate > 0,
                  },
                  {
                    label: `${formatDollar(locums?.callStipendWeekendRate)}/day Weekend Stipend`,
                    enabled: locums?.callStipendWeekendRate > 0,
                  },
                ],
              },
              {
                label: 'Night',
                value: nightlyHourlyRate,
                enabled: nightlyHourlyRate > 0,
              },
              {
                label: 'Weekend',
                value: weekendHourlyRate,
                enabled: weekendHourlyRate > 0,
              },
            ]
              .filter(({ enabled }) => enabled)
              .map(({ label, value, subItems }) => (
                <Fragment key={label}>
                  <div className="flex justify-between w-full">
                    <span>{label}</span>{' '}
                    <span className="text-copy-muted">
                      {formatDollar(value)}/hr
                    </span>
                  </div>
                  {Number(subItems?.length) > 0 && (
                    <div className="text-xs text-copy-muted font-normal flex flex-col gap-1">
                      {subItems
                        ?.filter(({ enabled }) => enabled)
                        .map((subItem) => (
                          <span key={subItem.label}>{subItem.label}</span>
                        ))}
                    </div>
                  )}
                </Fragment>
              ))}
          </div>
          {salaryDetails.otherNoteworthyComp ? (
            <DetailNote title="" className="my-4 text-xs">
              {salaryDetails.otherNoteworthyComp}
            </DetailNote>
          ) : null}
        </>
      ) : null}
      {showCollectionsPct || showWrvuRate || showHourlyRate ? (
        <span className="text-sm text-copy-muted ml-6">
          {showCollectionsPct ? (
            <>{collectionsPct}% of Total Collections</>
          ) : null}
          {showHourlyRate ? (
            <>
              {[
                {
                  label: 'Base Pay',
                  value: basePayPerHour,
                  enabled: basePayPerHour > 0,
                },
                {
                  label: 'Nights',
                  value: nightlyHourlyRate,
                  enabled: nightlyHourlyRate > 0 && hourlyRateDifferential,
                },
                {
                  label: 'Weekends',
                  value: weekendHourlyRate,
                  enabled: weekendHourlyRate > 0 && hourlyRateDifferential,
                },
              ]
                .filter(({ enabled }) => enabled)
                .map(
                  ({ label, value }) => `${label}: ${formatDollar(value)}/hr`,
                )
                .join('; ')}
            </>
          ) : null}
          {showWrvuRate ? (
            <>
              {new Intl.NumberFormat('en-US').format(compTargetWrvu)} wRVUs @{' '}
              {formatDollar(wrvuRate)}/wRVU
            </>
          ) : null}
        </span>
      ) : null}
    </div>
  )
}

function Glossary({
  label,
  subItems: _subItems,
  color,
  showHourly,
  isEnabled,
  defaultTotalValue,
}: {
  label: string
  subItems: SubItem[]
  color: string
  showHourly?: boolean
  isEnabled?: boolean
  defaultTotalValue?: number
}) {
  const subItems = _subItems || []

  const totalValue =
    defaultTotalValue || sum(subItems.map((item) => item.value))
  if (!totalValue || !isEnabled) {
    return null
  }

  const dataTestId =
    label === 'Bonuses'
      ? 'total-comp-bonuses'
      : label === 'Other Income'
        ? 'total-comp-other-income'
        : ''

  const firstItem = subItems[0]
  const singleItemLabel = firstItem?.singleItemLabel
  return (
    <div>
      <div className="flex w-full justify-between">
        <div className="flex space-x-2 items-center">
          <div className={`${color} w-4 h-4 rounded`} />
          <span className="font-medium text-sm text-copy-primary">
            {label}{' '}
            {subItems.length === 1 && singleItemLabel
              ? `(${
                  subItems[0].singleItemLabel + getOvertimeRateLabel(firstItem)
                })`
              : null}
          </span>
        </div>
        <span
          className="font-medium text-sm text-copy-muted"
          data-testid={dataTestId}
        >
          {showHourly
            ? `${formatDollar(totalValue)}/hr`
            : formatDollar(totalValue)}
        </span>
      </div>
      {subItems.length > 1 ? (
        <div className="flex">
          <span className="text-sm text-copy-muted ml-6">
            {subItems.map((item, index) => (
              <Fragment key={index}>
                <span>
                  {formatAbbreviatedDollar(item.value)} {item.label}
                  {getOvertimeRateLabel(item)}
                </span>
                {index < subItems.length - 1 ? ' + ' : null}
              </Fragment>
            ))}
          </span>
        </div>
      ) : null}
    </div>
  )
}

function TotalCompNumber({
  totalIncome,
  showHourly,
}: {
  totalIncome: number
  showHourly?: boolean
}) {
  return (
    <span
      className="text-copy-success font-bold shrink-0"
      data-testid="total-comp-income"
    >
      {showHourly
        ? `${formatDollar(totalIncome)}/hr`
        : `${formatDollar(totalIncome)}/yr`}
    </span>
  )
}

export function TotalCompDetails({
  salaryDetails,
  isOnboardingReviewPane,
  showHourly,
  accountType,
  isAuthenticated,
  isLocumsOrPrn,
}: TotalCompDetailsProps): React.JSX.Element {
  const posthog = usePostHog()
  const { professionField } = useProfessionField()
  const { base, bonus, other, total, annualBonus, annualOther } =
    salaryDetails.compTotal || {}
  const baseSalary = base || 0
  const totalBonuses = bonus || 0
  const totalOtherIncomes = other || 0
  const totalIncome = isLocumsOrPrn ? base || 0 : total || 0
  const totalAnnualBonus = annualBonus || 0
  const totalAnnualOther = annualOther || 0

  const salaryRestriction = useSalaryTableStore(
    (state) => state.salaryRestriction,
  )

  // ignore restriction when on onboarding review pane
  const _salaryRestriction = isOnboardingReviewPane ? null : salaryRestriction

  const groupedData = [
    {
      color: 'bg-blue-60',
      labels: ['Base Salary'],
      values: [(baseSalary / totalIncome) * 100],
      actual: [baseSalary],
    },
    {
      color: 'bg-purple-60',
      labels: ['Bonus'],
      values: isLocumsOrPrn ? [0] : [(totalBonuses / totalIncome) * 100],
      actual: isLocumsOrPrn ? [0] : [totalBonuses],
    },
    {
      color: 'bg-red-60',
      labels: ['Other Income'],
      values: isLocumsOrPrn ? [0] : [(totalOtherIncomes / totalIncome) * 100],
      actual: isLocumsOrPrn ? [0] : [totalOtherIncomes],
    },
  ]

  return (
    <div
      className="rounded-lg bg-card-highlight-bg dark:bg-background-muted/30
      border border-card-highlight-border mt-4"
    >
      <div className="relative">
        <TotalCompAlert
          salaryRestriction={_salaryRestriction}
          salaryId={salaryDetails.salaryId}
          moduleName="pane"
          isAuthenticated={isAuthenticated}
        />
        <div
          className={cn('p-4', {
            'blur-md pointer-events-none min-h-[220px]':
              Boolean(_salaryRestriction),
            'min-h-[300px]':
              _salaryRestriction === SalaryRestrictionType.SignedOut,
          })}
          aria-label="total compensation details"
        >
          <div className="flex justify-between items-center mb-1">
            <h3 className="font-medium text-copy-primary">
              {showHourly
                ? 'Total Hourly Compensation'
                : 'Total Cash Compensation'}
            </h3>
            {isOnboardingReviewPane ? (
              <EditReviewLink
                pageId={OnboardingPageIds.Compensation}
                className="justify-self-end"
                onClick={() => {
                  posthog.capture('clicked_comp_details_edit', {
                    screen_name: 'review_salary',
                    product_area: 'onboarding',
                    module_name: 'main',
                    profession_type: accountType,
                    profession: professionField,
                  })
                }}
              />
            ) : (
              <TotalCompNumber
                totalIncome={totalIncome}
                showHourly={showHourly || isLocumsOrPrn}
              />
            )}
          </div>
          <div className="flex justify-between items-center mb-1">
            {isOnboardingReviewPane ? (
              <p className="text-sm text-copy-accent">
                Comp Model ({formatCompModel(salaryDetails.compModel)})
              </p>
            ) : null}
            {isOnboardingReviewPane ? (
              <TotalCompNumber
                totalIncome={totalIncome}
                showHourly={showHourly || isLocumsOrPrn}
              />
            ) : null}
          </div>

          {isOnboardingReviewPane ? null : (
            <MiniPercentileHistogram
              totalIncome={totalIncome}
              compModel={salaryDetails.compModel}
            />
          )}
          <div className="flex w-full h-4 overflow-hidden">
            {groupedData.map((group, groupIndex) => {
              const groupTotal = group.values.reduce((a, b) => a + b, 0)
              if (!groupTotal) return null
              return (
                <React.Fragment key={groupIndex}>
                  {groupIndex > 0 && <div className="w-1" />}

                  <div
                    className={`flex ${group.values.length > 1 ? 'space-x-0.5' : ''}`}
                    style={{ width: `${groupTotal}%` }}
                  >
                    {group.values.map((value, index) => {
                      const valuePercentage = (value / groupTotal) * 100
                      return (
                        <TooltipProvider key={`${groupIndex}-${index}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  group.color,
                                  index === 0 && 'rounded-l-sm',
                                  index === group.values.length - 1 &&
                                    'rounded-r-sm',
                                )}
                                style={{ width: `${valuePercentage}%` }}
                              />
                            </TooltipTrigger>
                            <TooltipContent
                              className={`bg-background-primary p-2 rounded border text-center 
                        border-background-muted`}
                            >
                              <div className="font-bold text-base">
                                {formatAbbreviatedDollar(group.actual[index])}
                              </div>
                              <div className="text-xs">
                                {group.labels[index]}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    })}
                  </div>
                </React.Fragment>
              )
            })}
          </div>
          <div className="mt-4 flex flex-col space-y-2 mb-0">
            <SalaryGlossary
              salaryDetails={salaryDetails}
              isLocumsOrPrn={isLocumsOrPrn}
            />
            <Glossary
              label="Bonuses"
              subItems={salaryDetails.compBonuses}
              color="bg-purple-60"
              showHourly={false} // always show annual bonus in salary pane
              isEnabled={totalBonuses > 0 && !isLocumsOrPrn}
              defaultTotalValue={showHourly ? totalAnnualBonus : totalBonuses}
            />
            <Glossary
              label="Other Income"
              subItems={salaryDetails.otherIncomes}
              color="bg-red-60"
              showHourly={false} // always show annual other income in salary pane
              isEnabled={totalOtherIncomes > 0 && !isLocumsOrPrn}
              defaultTotalValue={
                showHourly ? totalAnnualOther : totalOtherIncomes
              }
            />
          </div>
          {(salaryDetails.ancillaryIncomeSource &&
            salaryDetails.annualEstAncillaryIncome) ||
          salaryDetails.disclaimer ? (
            <div className="flex flex-col gap-2 mt-4">
              {salaryDetails.ancillaryIncomeSource &&
              salaryDetails.annualEstAncillaryIncome ? (
                <div className="text-sm text-copy-muted">
                  * {salaryDetails.ancillaryIncomeSource}
                </div>
              ) : null}
              {salaryDetails.disclaimer ? (
                <div className="text-sm text-copy-muted">
                  {salaryDetails.disclaimer}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {salaryDetails.compSatisfaction?.enabled && (
        <div className="flex p-4 border-t border-border-muted">
          <p className="text-sm">
            <SatisfactionBadge
              compSatisfaction={salaryDetails.compSatisfaction.id}
            />{' '}
            <span className="text-copy-muted">with Total Compensation</span>
          </p>
        </div>
      )}
    </div>
  )
}
